// Verbatim copy of customer/web/src/util/imageCompression.ts — ops/web is a separate
// workspace and /shared/ is contractually limited to cross-system event schemas and build config.

/**
 * Client-side photo downscaling for every upload path (RFQ images, Property
 * File media, property icons).
 *
 * Photos are the most valuable thing a customer gives us, and a modern phone
 * produces 8-20 MB originals. Asking an end user to go compress an image
 * themselves loses the request, so we shrink in the browser instead and keep a
 * generous byte cap purely as a backstop.
 *
 * Two constraints shape the design:
 *
 * 1. CONTENT-TYPE IS SIGNED. The server binds Content-Type into the presigned
 *    PUT and re-checks it with a strict equality test against the S3 HEAD
 *    (rfq-images.ts verifyUploadedImage). So when compression turns a HEIC into
 *    a JPEG, the new type has to be decided BEFORE presign — hence
 *    `compressImageForUpload` returns the content type alongside the file and
 *    every caller presigns with what it returns, never with `file.type`.
 *
 * 2. HEIC IS ONLY DECODABLE ON SAFARI. Chrome and Firefox ship no HEIC codec,
 *    so an iPhone HEIC opened on desktop Chrome cannot be re-encoded at all.
 *    Every failure path therefore falls back to passing the ORIGINAL file
 *    through untouched rather than blocking the upload — the user still gets
 *    their request in, just over more bytes. This is why MAX_UPLOAD_BYTES has
 *    to stay well above a typical original.
 */

/** MIME types the API accepts for photo uploads (mirrors the server allowlist). */
export const UPLOAD_IMAGE_MIMES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;
export type UploadImageMime = (typeof UPLOAD_IMAGE_MIMES)[number];

/**
 * 25 MiB — the backstop, not the target. Must stay <= the server cap
 * (customer/api/src/domain/media-limits.ts) because the browser PUTs to S3
 * before it calls attach: a client cap above the server's would spend the
 * user's upload and only then fail.
 */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Long-edge ceiling. Ample for a contractor diagnosing a leak on a phone. */
const MAX_DIMENSION = 2048;

/** JPEG quality. 0.82 is the knee — visually clean, ~10x smaller than q1.0. */
const JPEG_QUALITY = 0.82;

/**
 * Below this, a canvas round-trip isn't worth it: the upload is already fast
 * and re-encoding small flat images (screenshots, line art) can make them
 * BIGGER.
 */
const COMPRESS_MIN_BYTES = 512 * 1024;

/** Give up on a wedged decoder rather than hanging the wizard. */
const DECODE_TIMEOUT_MS = 15_000;

/**
 * Some browsers report an EMPTY `file.type` for .heic/.heif — notably iOS
 * Safari edge cases and any desktop browser without a HEIC codec. Fall back to
 * the extension so those uploads aren't wrongly rejected as "unsupported".
 */
export function detectUploadMime(file: File): UploadImageMime | undefined {
  if ((UPLOAD_IMAGE_MIMES as readonly string[]).includes(file.type)) {
    return file.type as UploadImageMime;
  }
  if (file.type === "") {
    const ext = file.name.toLowerCase().split(".").pop();
    if (ext === "heic") return "image/heic";
    if (ext === "heif") return "image/heif";
  }
  return undefined;
}

/** Scale (w, h) down to fit a square of `max`, preserving aspect ratio. */
export function fitWithin(
  width: number,
  height: number,
  max: number,
): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** `IMG_0042.HEIC` → `IMG_0042.jpg`. Extensionless names just gain `.jpg`. */
export function toJpegFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem || "photo"}.jpg`;
}

export type CompressOutcome =
  | "compressed"
  | "skipped-small"
  | "skipped-undecodable"
  | "skipped-larger"
  | "skipped-aborted";

export interface CompressedUpload {
  file: File;
  contentType: UploadImageMime;
  outcome: CompressOutcome;
}

/** Re-encode step, injectable so the decision logic is testable without a canvas. */
export type Reencoder = (file: File) => Promise<Blob | null>;

/**
 * Decode with an `<img>` + object URL, draw to a canvas, re-encode as JPEG.
 * Resolves `null` on ANY failure — the caller treats that as "upload the
 * original".
 *
 * `<img>` rather than `createImageBitmap` is deliberate, for two reasons:
 *
 *  - EXIF ORIENTATION. Since Chrome 81 / Firefox 77 / Safari 13.1 the CSS
 *    default `image-orientation: from-image` means a decoded HTMLImageElement
 *    already reports orientation-APPLIED dimensions and draws upright, so the
 *    canvas output needs no transform matrix and no EXIF parser.
 *    `createImageBitmap` defaults to `imageOrientation: "none"` — it would
 *    silently upload sideways photos.
 *  - HEIC. Safari CAN decode HEIC in an `<img>`, which covers the iPhone users
 *    who hit this most; Chrome/Firefox fire `onerror`, which is exactly the
 *    fast, clean failure signal we want.
 *
 * The re-encode also strips GPS EXIF, which is a small privacy win.
 */
async function canvasReencode(file: File): Promise<Blob | null> {
  let canvas: HTMLCanvasElement | undefined;
  let url: string | undefined;
  try {
    // Probe before touching the file: jsdom (and any canvas-less environment)
    // returns null here, so tests and old browsers bail instantly instead of
    // waiting on an <img> that will never load.
    canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    url = URL.createObjectURL(file);
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("decode timeout")), DECODE_TIMEOUT_MS);
      img.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      img.onerror = () => {
        clearTimeout(timer);
        reject(new Error("decode failed"));
      };
      img.src = url!;
    });

    const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, MAX_DIMENSION);
    if (width < 1 || height < 1) return null;
    canvas.width = width;
    canvas.height = height;
    ctx.imageSmoothingQuality = "high";
    // We always emit JPEG, which has no alpha — paint white first so a
    // transparent PNG comes out on white rather than the black a naive
    // re-encode produces.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    return await new Promise<Blob | null>((resolve) => {
      const timer = setTimeout(() => resolve(null), DECODE_TIMEOUT_MS);
      canvas!.toBlob(
        (blob) => {
          clearTimeout(timer);
          resolve(blob);
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    });
  } catch {
    return null;
  } finally {
    if (url) URL.revokeObjectURL(url);
    // iOS Safari is aggressive about canvas memory and the RFQ wizard can hold
    // eight 12 MP photos — drop the backing store as soon as we're done.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}

/**
 * Shrink `file` for upload, returning the bytes to PUT and the content type to
 * presign with. NEVER throws and never rejects a file: on any failure it hands
 * back the original unchanged, so a photo the browser can't decode still
 * reaches the request.
 *
 * Callers MUST presign and set the PUT `Content-Type` from the returned
 * `contentType`, and report the returned `file.size` on attach.
 */
export async function compressImageForUpload(
  file: File,
  contentType: UploadImageMime,
  opts: { signal?: AbortSignal; reencode?: Reencoder } = {},
): Promise<CompressedUpload> {
  const passthrough = (outcome: CompressOutcome): CompressedUpload => ({
    file,
    contentType,
    outcome,
  });

  if (opts.signal?.aborted) return passthrough("skipped-aborted");
  if (file.size <= COMPRESS_MIN_BYTES) return passthrough("skipped-small");

  // Belt and braces: canvasReencode already swallows its own failures, but an
  // out-of-memory throw on a huge image must degrade to "upload the original"
  // rather than take down the submit.
  let blob: Blob | null;
  try {
    blob = await (opts.reencode ?? canvasReencode)(file);
  } catch {
    blob = null;
  }

  // The user may have cancelled while we were encoding.
  if (opts.signal?.aborted) return passthrough("skipped-aborted");
  if (!blob) return passthrough("skipped-undecodable");
  // Re-encoding isn't guaranteed to help (already-optimized JPEGs, flat PNGs).
  if (blob.size >= file.size) return passthrough("skipped-larger");

  return {
    file: new File([blob], toJpegFileName(file.name), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    }),
    contentType: "image/jpeg",
    outcome: "compressed",
  };
}
